import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { CreateInvoiceDto, Invoice } from "../../types/invoice.types";
import { AppTheme } from "../../AppTheme";
import {
  getInvoiceById,
  updateInvoice,
} from "../../service/invoice/InvoiceServices";
import { useDispatch } from "react-redux";
import { getClientById } from "../../service/client/ClientServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faEye, faXmark } from "@fortawesome/free-solid-svg-icons";
import { createItem } from "../../service/item/ItemServices";
import { InvoiceForm } from "../../components/Invoice/InvoiceForm";
import { invoiceData } from "@/data/invoiceData";

export const EditInvoice: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const [clientInfo, setClientInfo] = useState<any>();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        const invoiceData = await getInvoiceById(dispatch, id);
        if (!invoiceData) return;
        const clientInfo = await getClientById(dispatch, invoiceData.client_id);
        const formattedClients = [{ ...clientInfo }].map((client: any) => ({
          id: client.client_id,
          name: client.client_name,
          email: client.email,
          contactPerson: client.contact_person,
          phone: client.phone,
          address: {
            line1: client.address_line1,
            line2: client.address_line2,
            city: client.city,
            stateProvince: client.state_province,
            postalCode: client.postal_code,
            country: client.country_name,
          },
          taxNumber: client.tax_number,
          taxExempt: client.tax_exempt,
          payment_terms: client.payment_term_id,
        }));

        invoiceData.total = parseFloat(invoiceData.total);
        invoiceData.subtotal = parseFloat(invoiceData.total);
        invoiceData.tax_total = parseFloat(invoiceData.tax_total);
        invoiceData.amount_paid = parseFloat(invoiceData.amount_paid);
        setInvoice(invoiceData);
        setClientInfo(formattedClients[0]);
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load invoice",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [id, dispatch]);

  const formatInvoiceData = (data: CreateInvoiceDto): CreateInvoiceDto => {
    const invoiceItems =
      data.invoice_items?.map((item) => ({
        item_id: item.item_id ? parseInt(item.item_id.toString()) : undefined,
        item_code: item.item_code || "",
        item_name: item.item_name || "",
        sacCode: item.sacCode || "",
        sac_gst_rate: item.sac_gst_rate || 0,
        taxName: item.taxName || "",
        description: item.description || "",
        quantity: item.quantity || 0,
        unit_price:
          typeof item.unit_price === "string"
            ? parseFloat(item.unit_price)
            : item.unit_price,
        unit_type: item.unit_type || "",
        tax_rate_id: item.tax_rate_id || undefined,
        sort_order: item.sort_order || 0,
        discount: item.discount || 0,
      })) || [];

    return {
      client_id: parseInt(data.client_id?.toString() || "0"),
      clientName: data?.clientName,
      currency_code: data?.currency_code || "USD",
      issue_date: data.issue_date || new Date(),
      due_date: data.due_date || new Date(),
      currency_id: data.currency_id || 1,
      industry_id: data.industry_id,
      tax_inclusive: data.tax_inclusive || false,
      payment_terms: data.payment_term_id,
      payment_term_id: data.payment_term_id,
      invoiceNumber: data?.invoiceNumber,
      status: data?.status,
      business_type_id: data.business_type_id,
      notes: data.notes,
      terms_conditions: data.terms_conditions,
      template_id: data.template_id,
      choosedTemplateId: data.choosedTemplateId,
      is_recurring: data.is_recurring || false,
      parent_invoice_id: data.parent_invoice_id,
      recurrence_pattern: data.recurrence_pattern,
      next_recurrence_date: data.next_recurrence_date,
      business_type: data.business_type,
      invoice_items: invoiceItems,
      emailTo: data.emailTo,
      emailCc: data.emailCc,
      emailMessage: data?.emailMessage,
      emailSubject: data.emailSubject,
      natureOfPayment: data.natureOfPayment || null,
      threashold: data.threashold || null,
      gstType: data.gstType || null,
      gstRate: data.gstRate || 0,
      yearOfGst: data.yearOfGst || null,
      documents: data.documents,
      withPaymentLink: data.withPaymentLink,
      round_off: data.round_off
    };
  };
  const handleSubmit = async (data: CreateInvoiceDto,skipNavigate:boolean=false) => {
    setIsSubmitting(true);
    try {
      // Format data for API
      const invoiceData: any = formatInvoiceData(data);
      const missingItems: any = data.invoice_items
        .filter((item: any) => !item?.item_id) // Filter items where item_id does not exist
        .map((item: any) => ({
          item_code: null,
          item_name: item.item_name || "",
          sacCode: item.sacCode || null,
          sac_gst_rate: item.sac_gst_rate || 0,
          description: item.description || "",
          quantity: item.quantity || 0,
          unit_price: item.unit_price || 0,
          unit_type: item.unit_type || "",
          tax_rate_id: item.tax_rate_id || undefined,
          sort_order: item.sort_order || 0,
          discount: item.discount || 0,
        }));
      if (missingItems?.length > 0) {
        const createdItems = await createItem(dispatch, missingItems);
        if (Array.isArray(createdItems?.result)) {
          // Update invoice_items with newly created item_ids
          invoiceData.invoice_items = data.invoice_items.map((item: any) => {
            if (!item?.item_id) {
              const matched = createdItems.result.find(
                (created: any) => created.item_name === item.item_name
              );
              if (matched) {
                const { isNew, ...otherItemProperties } = item;
                return { ...otherItemProperties, item_id: matched.item_id };
              }
            }
            const { isNew, ...otherItemProperties } = item;
            return otherItemProperties;
          });
        }
      }

      // Call the API
      const response = await updateInvoice(
        dispatch,
        invoiceData,
        id as unknown as number
      );

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Invoice updated successfully",
          life: 3000,
        });

        // Simulate API call with delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Show success message
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Invoice updated successfully",
          life: 3000,
        });

        // After successful update, redirect to invoice detail after a short delay
        setTimeout(() => {
          if(!skipNavigate){
 
            navigate(`/invoice/${id}`);
          }

        }, 1500);
      }
    } catch (error) {
      // Show error message
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update invoice. Please try again.",
        life: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-6">
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        <span className="ml-3">Loading invoice data...</span>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-medium text-gray-700">Invoice not found</h2>
        <p className="text-gray-600 mt-2">
          The requested invoice could not be found or you don't have permission
          to view it.
        </p>
        <Button className="mt-4" onClick={() => navigate("/invoice")}>
          <FontAwesomeIcon icon={faArrowLeft} />
          {"  "}
          Back to Invoices
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Toast ref={toast} />

      <div className="flex justify-between items-center mb-6">
        <h1
          className={`text-${AppTheme.font.size["2xl"]} font-bold text-gray-800`}
        >
          Edit Invoice {invoice.invoice_number}
        </h1>

        <div className="flex gap-2">
          <Button
            className="p-button-outlined"
            onClick={() => navigate(`/invoice/${id}`)}
          >
            <FontAwesomeIcon icon={faXmark} />
            {"  "}
            Cancel
          </Button>
          <Button
            className="p-button-outlined"
            onClick={() => navigate(`/invoice/${id}`)}
          >
            <FontAwesomeIcon icon={faEye} />
            {"  "}
            View Invoice
          </Button>
        </div>
      </div>

      <InvoiceForm
        initialData={invoice}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        mode="edit"
        preselectedClient={clientInfo}
      />
    </div>
  );
};
